import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MenuController, NavController, Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { ClinicQuery } from '@state/clinic/clinic.query';
import { EMPTY, Observable, Subscription, timer } from 'rxjs';
import { Clinic } from '@state/clinic/clinic.model';
import { ClinicService } from '@state/clinic/clinic.service';
import { SessionService } from '@state/session/session.service';
import { SessionQuery } from '@state/session/session.query';
import { delay, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { UiQuery } from '@state/ui/ui.query';
import { Preferences }  from '@capacitor/preferences';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Config, initializeApp} from '@flowww/design-system';
import { FLOW_DATE_FORMAT, FLOW_TIME_FORMAT, SESSION_ERROR_IDS } from '@core/constants';
// import { Keyboard } from '@capacitor/keyboard';
import { UiService } from '@core/state/ui/ui.service';
import { KeyboardState } from '@core/models/KeyboardState';
import { register } from 'swiper/element/bundle';
import {InitFlowwwService} from '@core/services/initFlowww/initFlowww.service';
import {SessionInterceptor} from '@core/interceptors/session/session.interceptor';

register();
@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    @ViewChild('routerOutlet') private routerOutlet: ElementRef;

    clinics$: Observable<Clinic[]>;
    loading$: Observable<boolean>;
    manualLoading$: Observable<boolean>;
    skeleton$: Observable<boolean>;
    selectedClinic: Clinic;
    username$: Observable<string>;

    isLogged$: Observable<boolean>;
    labelsLoaded$: Observable<boolean>;

    private selectedClinicSubscription: Subscription;
    private userLanguageSubscription: Subscription;
    private keyboardListeners: any[] = [];

    constructor(
        private clinicQuery: ClinicQuery,
        private clinicService: ClinicService,
        private menuController: MenuController,
        private navController: NavController,
        private platform: Platform,
        private sessionQuery: SessionQuery,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private uiQuery: UiQuery,
        private uiService: UiService,
        private initFlowwwService: InitFlowwwService,
        private sessionInterceptor: SessionInterceptor
    ) {
        Preferences.migrate();
        this.initializeApp();
        this.clinics$ = this.clinicQuery.selectAll();
        this.loading$ = this.uiQuery.selectLoading().pipe(delay(0));
        this.manualLoading$ = this.uiQuery.selectManualLoading().pipe(delay(0));
        this.skeleton$ = this.uiQuery.selectSkeleton().pipe(delay(0));
        this.username$ = this.sessionQuery.select('userName');
        this.isLogged$ = this.sessionQuery.select('aesUrl').pipe(map((aesUrl) => aesUrl !== ''));
        this.labelsLoaded$ = this.uiQuery.selectLabelsLoaded();
    }

    initializeApp(): void {
        const configDS: Config = initializeApp({dateFormat: FLOW_DATE_FORMAT, timeFormat: FLOW_TIME_FORMAT});

        this.userLanguageSubscription = this.sessionQuery.select('language').subscribe(lang => {
            this.translateService.setDefaultLang('en');
            this.translateService.use(lang);

            this.translateService.onTranslationChange.subscribe((data)=>{
                configDS.set('translations', data.translations);
            });
        });

        this.platform.ready().then(() => {
            if (Capacitor.isNativePlatform()) {
                try {
                    // En Android 15+ setBackgroundColor es no-op: el fondo lo pinta
                    // el plugin EdgeToEdge (config backgroundColor de capacitor.config.ts)
                    StatusBar.setBackgroundColor({
                        color: '#FFFFFF'
                    });
                    StatusBar.setStyle({
                        style: Style.Light
                    });

                    if (Capacitor.getPlatform() === 'ios') {
                        StatusBar.setOverlaysWebView({
                            overlay: false
                        });
                    }
                } catch (error) {
                    // Un fallo de StatusBar no debe abortar la inicialización
                }

                SplashScreen.hide();
            }
        });
    }

    async ngOnInit(): Promise<void> {
        await this.setupKeyboardListeners();

        // Safety net: si el loading lleva más de 30s activo sin sesión, forzar logout
        this.setupSessionSafetyNet();

        this.sessionQuery.select('aesUrl').pipe(
            filter(url => !!url),
            take(1) // para que se ejecute solo una vez cuando se refresca
        ).subscribe(() => {
            // movido el initFlowwwService al app.comp para globalizar la carga de labels al refrescar la pág
            this.initFlowwwService.init().subscribe({
                next: () => {
                    this.uiService.setLabelsLoaded(true);
                },
                error: (err) => {
                    // Solo los errores de sesión justifican destruir la sesión almacenada:
                    // un timeout o un fallo transitorio de red aquí borraría una sesión
                    // válida. Logout debounced: el DataInterceptor puede haberlo lanzado
                    // ya para este mismo fallo
                    if (SESSION_ERROR_IDS.includes(err)) {
                        this.sessionInterceptor.handleSessionExpired();
                    }
                }
            });
        });

        this.selectedClinicSubscription = this.clinicQuery.selectActive()
            .pipe(filter(clinic => clinic !== undefined))
            .subscribe((clinic: Clinic) => {
                this.selectedClinic = clinic;
            });
    }

    private setupSessionSafetyNet(): void {
        // Si el loading lleva más de 90s activo ininterrumpidamente y no hay sesión activa, algo falló.
        // switchMap cancela el timer en cuanto el loading baja: no dispara con loadings ya resueltos
        // (p.ej. usuario parado en la pantalla de login tras una petición puntual).
        // `logged` (userId) solo se setea al cargar settings: con un init en vuelo
        // un arranque legítimo lento aún no está logueado y no hay que desloguearlo
        this.uiQuery.selectLoading().pipe(
            distinctUntilChanged(),
            switchMap(loading => loading ? timer(90000) : EMPTY),
            filter(() => !this.sessionQuery.logged && !this.initFlowwwService.initInProgress)
        ).subscribe(() => {
            this.sessionService.systemLogout();
        });
    }

    clinicCompareFunction(clinic1: Clinic, clinic2: Clinic): boolean {
        return clinic1 && clinic2 && clinic1.id === clinic2.id;
    }

    async logout(): Promise<void> {
        await this.sessionService.systemLogout();
    }

    selectClinic({ id }: Clinic): void {
        this.clinicService.initClinic(id).subscribe({
            next: () => {
                this.navController.navigateRoot(['/agenda']);
            },
            error: (error) => {
                // console.error('❌ Error inicializando clínica:', error);
            }
        });

    }

    async ngOnDestroy(): Promise<void> {
        this.selectedClinicSubscription.unsubscribe();
        this.userLanguageSubscription.unsubscribe();
        await this.removeKeyboardListeners();
    }

    private async setupKeyboardListeners(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            const { Keyboard } = await import('@capacitor/keyboard');

            // Guardar referencias de los listeners
            const listener1 = await Keyboard.addListener('keyboardWillShow', info => {
                this.uiService.setKeyboardState(KeyboardState.KeyboardWillShow);
            });

            const listener2 = await Keyboard.addListener('keyboardDidShow', info => {
                this.uiService.setKeyboardState(KeyboardState.KeyboardDidShow);
            });

            const listener3 = await Keyboard.addListener('keyboardWillHide', () => {
                this.uiService.setKeyboardState(KeyboardState.KeyboardWillHide);
            });

            const listener4 = await Keyboard.addListener('keyboardDidHide', () => {
                this.uiService.setKeyboardState(KeyboardState.KeyboardDidHide);
            });

            // Guardar todas las referencias
            this.keyboardListeners = [listener1, listener2, listener3, listener4];
        }
    }
    private async removeKeyboardListeners(): Promise<void> {
        if (Capacitor.isNativePlatform() && this.keyboardListeners.length > 0) {
            try {
                for (const listener of this.keyboardListeners) {
                    await listener?.remove();
                }
                this.keyboardListeners = [];
            } catch (error) {
                // console.warn('Error removing keyboard listeners:', error);
            }
        }
    }

}
